import { useContext, useMemo } from "react";
import { AuthContext } from "../AuthProvider";
import { usePermissionsCtx } from "../authz/PermissionsProvider";
import { canFromGrants } from "../authz/can";
import { STAFF_SCOPES } from "../authz/PermissionScope";
import { canEnterStaffPortalFromGrants } from "../authz/staffPortalAccess";
import { useSupportViewAs } from "../supportViewAs/SupportViewAsProvider";
import { hasParentPortalRole } from "./startPagePreference";

const MY_ENVIRONMENT_CHECKS = [
  { resource: "my.environment", action: "view" },
  { resource: "my.teacher-details", action: "view" },
  { resource: "my.classrooms", action: "view" },
  { resource: "my.parent-meetings", action: "view" },
];

/**
 * Which start pages the signed-in user can open. Staff pages only count ANY/OWN grants,
 * also when this is used outside the staff shell (home redirect, parent navbar, profile).
 */
export function useStartPageAccess() {
  const auth = useContext(AuthContext);
  const { ready, grants } = usePermissionsCtx() || {};
  const supportViewAs = useSupportViewAs();
  const tokenParsed = supportViewAs?.active ? null : auth?.keycloak?.tokenParsed;
  const userRoles = auth?.userRoles;

  return useMemo(() => {
    const can = (resource, action) =>
      canFromGrants({ ready, grants, tokenParsed, resource, action, scopes: STAFF_SCOPES });
    return {
      canEnterStaffPortal: canEnterStaffPortalFromGrants({ ready, grants, tokenParsed }),
      canViewDashboard: can("dashboard", "view"),
      canViewMyEnvironment: MY_ENVIRONMENT_CHECKS.some((check) => can(check.resource, check.action)),
      canViewReports: can("reports.root", "view"),
      canViewClassrooms: can("school.classrooms", "view"),
      canViewWaitingList: can("school.waiting-list", "view"),
      canUseParentPortal: hasParentPortalRole(userRoles),
    };
  }, [ready, grants, tokenParsed, userRoles]);
}
